import React, { useCallback, useState } from 'react'
import { TaskInput, TaskOutput } from '~/types/queenbee/io'
import { AtSymbol, Document } from '../icons'

type TaskParameter = TaskInput | TaskOutput

type TaskNodeParameterProps = {
  mode: 'input' | 'output'
  parameter: TaskParameter
}


export const TaskNodeParameter = ({
  mode,
  parameter,
}: TaskNodeParameterProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)

  const isParameter = useCallback((parameter: TaskParameter): boolean => {
    return parameter.type === 'TaskArgument'
  }, [])

  const isArtifact = useCallback((parameter: TaskParameter): boolean => {
    return parameter.type === 'TaskPathArgument'
  }, [])

  const getParameterInfo = useCallback(
    (p: TaskParameter): { label: string; value?: string; detail?: string } => {
      if (isParameter(p)) {
        // const detail = p.from
        return {
          label: p.name,
          // value: detail ? (detail?.length < 35 ? detail : `${detail.substring(0, 30)}..`) : undefined,
          // detail: detail,
        }
      } else if (isArtifact(p)) {
        // const detail = (p as InputArtifact).subpath ?? (p as OutputArtifact).path ?? ''
        return {
          label: p.name,
          // value: detail.length < 35 ? detail : `${detail.substring(0, 30)}..`,
          // detail: detail,
        }
      } else {
        return { label: (p as TaskParameter).name }
      }
    },
    [isParameter, isArtifact]
  )

  const getIcon = useCallback((parameter: TaskParameter): JSX.Element => {
    if (isParameter(parameter)) {
      return <AtSymbol mode="dark" filled={false} />
    } else {
      return <Document mode="dark" filled={false} />
    }
  }, [])

  const { label, value, detail } = getParameterInfo(parameter)
  const icon = getIcon(parameter)

  const handleClick = (): void => {
    if (!value) {
      return
    }
    setIsOpen((open) => !open)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter') {
      setIsOpen((open) => !open)
    }
  }

  return (
    <div className={`task-node-io-entry ${mode === 'output' ? 'task-node-io-entry--output' : ''}`}>
      {mode === 'input' ? (
        <>
          <div className="task-node-io-icon task-node-io-icon--input">{icon}</div>
          <div
            className={`task-node-io-label ${detail ? 'task-node-io-label--expandable' : ''} ${
              isOpen ? 'task-node-io-label--open' : ''
            }`}
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
          >
            <div className="task-node-io-label-name">{label}</div>
            {detail ? (
              <div className="task-node-io-label-value">
                <span title={detail}>{value}</span>
              </div>
            ) : null}
          </div>
          {detail ? (
            <button className="task-detail-toggle" onClick={() => setIsOpen((open) => !open)}>
              <svg width={12} height={12} viewBox="-2 -2 14 14">
                <polyline
                  points={isOpen ? '0,7.5 5,2.5 10,7.5' : '0,3.5 5,8.5 10,3.5'}
                  fill="none"
                  strokeWidth="2px"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : null}
        </>
      ) : (
        <>
          <div className="task-node-io-icon task-node-io-icon--output">{icon}</div>
          <div
            className={`task-node-io-label task-node-io-label--output ${
              detail ? 'task-node-io-label--expandable' : ''
            } ${isOpen ? 'task-node-io-label--open' : ''}`}
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
          >
            <div className="task-node-io-label-name">{label}</div>
            {detail ? (
              <div className="task-node-io-label-value">
                <span title={detail}>{value}</span>
              </div>
            ) : null}
          </div>
          {detail ? (
            <button className="task-detail-toggle" onClick={() => setIsOpen((open) => !open)}>
              <svg width={12} height={12} viewBox="-2 -2 14 14">
                <polyline
                  points={isOpen ? '0,7.5 5,2.5 10,7.5' : '0,3.5 5,8.5 10,3.5'}
                  fill="none"
                  strokeWidth="2px"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : null}
        </>
      )}
      <style global jsx>{`
        .task-node-io-icon--input > svg {
          transform: rotate(-45deg);
        }

        .task-node-io-icon--output > svg {
          transform: rotate(135deg);
        }
      `}</style>
      <style jsx>{`
        .task-node-io-entry {
          margin-top: 12px;
          width: 100%;
          display: flex;
          flex-direction: row;
          justify-content: flex-start;
          align-items: flex-start;
        }

        .task-node-io-entry--output {
          justify-content: flex-end;
        }

        .task-node-io-icon {
          width: 18px;
          height: 18px;
          box-sizing: border-box;
          border: 2px solid #333;
          border-radius: 18px;
          border-top-right-radius: 0;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .task-node-io-icon--input {
          margin-right: 9px;
          transform: rotate(45deg);
        }

        .task-node-io-icon--output {
          margin-right: 9px;
          transform: rotate(-135deg);
        }

        .task-node-io-label {
          flex-grow: 1;
          max-height: 18px;
          padding-left: 9px;
          padding-right: 9px;
          border-radius: 9px;
          font-family: 'Space Mono', monospace;
          font-size: 9px;
          line-height: 18px;
          overflow: hidden;
          transition: 300ms;
          transition-timing-function: ease;
        }

        .task-node-io-label--open {
          max-height: 50px;
        }

        .task-node-io-label-name {
          font-size: 11px;
          line-height: 16px;
          margin-bottom: 9px;
          white-space: nowrap;
        }

        .task-node-io-label-value {
          font-size: 11px;
          line-height: 16px;
          margin-bottom: 9px;
          color: #333;
          white-space: nowrap;
        }

        .task-detail-toggle {
          width: 18px;
          min-width: 18px;
          height: 18px;
          min-height: 18px;
          padding: 0;
          margin-left: 9px;
          border-radius: 18px;
          border: 2px solid #333;
          background: none;
          display: flex;
          justify-content: center;
          align-items: center;
          transition: 300ms;
          transition-timing-function: ease;
        }

        .task-detail-toggle:hover {
          cursor: pointer;
          border: 2px solid #333;
        }

        .task-detail-toggle > svg > polyline {
          stroke: #333;
          transition: 300ms;
          transition-timing-function: ease;
        }

        .task-detail-toggle:hover > svg > polyline {
          stroke: #333;
        }
      `}</style>
    </div>
  )
}
